// eslint-disable-next-line import/no-extraneous-dependencies
import React, { ComponentType, useEffect, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { createClient } from '@wix/sdk';
import { dashboard, type RemoteHost, type LocalHost } from './index';

const useBeforeRenderEffect = <T extends unknown>(
  effect: () => T,
  effectCondition: (lastEffectResult: T) => boolean,
): T => {
  const [lastEffectResult, setState] = useState(() => effect());
  if (effectCondition(lastEffectResult)) {
    setState(effect());
  }
  return lastEffectResult;
};

export const withContextualWixClient = <P extends {} = {}>(
  Component: ComponentType<P>,
): ComponentType<P & { host?: LocalHost }> => {
  return (ownPropsWithHost) => {
    const { host, compProps } = useHostAndProps(ownPropsWithHost);

    useBeforeRenderEffect(
      () => {
        createClient({
          auth: dashboard.auth(),
          host,
        }).enableContext('module');
        return host;
      },
      (lastHost) => lastHost !== host,
    );

    return compProps ? <Component host={host} {...compProps} /> : null;
  };
};

const useHostAndProps = <P extends {}>(
  ownPropsWithHost: P & { host?: LocalHost },
):
  | { host: LocalHost; compProps: P }
  | {
      host: RemoteHost;
      compProps: P | undefined;
    } => {
  const { host: maybeLocalHost, ...ownProps } = ownPropsWithHost;

  const { host, hostType } = useBeforeRenderEffect(
    () =>
      maybeLocalHost
        ? { host: maybeLocalHost, hostType: 'local' as const }
        : { host: dashboard.host(), hostType: 'remote' as const },
    (lastResult) => !!maybeLocalHost && maybeLocalHost !== lastResult.host,
  );

  const [hostProps, setHostProps] = useState<P | undefined>(undefined);

  useEffect(() => {
    if (hostType === 'local') {
      return;
    }

    const observeResult = host.channel.observeState((newPropsFromHost: any) => {
      setHostProps((newPropsFromHost || {}) as P);
    });

    const cleanup = async () => {
      (await observeResult).disconnect();
      host.close();
    };

    return () => {
      cleanup();
    };
  }, [host, hostType]);

  return hostType === 'local'
    ? { host, compProps: ownProps as P }
    : {
        host,
        compProps: hostProps ? { ...hostProps, ...ownProps } : undefined,
      };
};
